import { useState } from "react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const PRESETS = [
  { tons: 500, label: "Small team" },
  { tons: 2500, label: "Mid-market" },
  { tons: 10000, label: "Enterprise" },
  { tons: 50000, label: "Global brand" },
];

export default function SuggestPortfolio() {
  const [tons, setTons] = useState(2500);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [added, setAdded] = useState(false);
  const { add: addToCart, clear } = useCart();
  const { format } = useCurrency();

  const run = async () => {
    if (!tons || tons < 1) return;
    setLoading(true); setResult(null); setAdded(false);
    try {
      const { data } = await api.post("/portfolio/suggest", { total_tons: Number(tons) });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggestion failed");
    } finally { setLoading(false); }
  };

  const addAll = () => {
    if (!result) return;
    clear();
    result.items.forEach((i) => addToCart(i.project, i.tons));
    setAdded(true);
    toast.success(`Added ${result.items.length} projects to your portfolio`);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#D97757]"/>
        <div className="text-sm font-medium">Auto-suggest a portfolio</div>
      </div>
      <p className="text-sm text-stone-600 mb-4">Tell us your annual tons target — we'll diversify across categories using the cheapest verified project in each.</p>

      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => (
          <button key={p.tons} onClick={() => setTons(p.tons)} data-testid={`suggest-preset-${p.tons}`} className={`px-3 py-1.5 rounded-full text-xs border ${tons === p.tons ? "bg-[#2C5530] text-white border-[#2C5530]" : "bg-white border-stone-200"}`}>
            {p.label} · {p.tons.toLocaleString()}t
          </button>
        ))}
      </div>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Label htmlFor="s-tons" className="text-xs uppercase tracking-widest text-stone-500">Tons per year</Label>
          <Input id="s-tons" type="number" min={1} value={tons} onChange={(e) => setTons(Number(e.target.value))} className="mt-1 h-10 bg-stone-50 border-stone-200" data-testid="suggest-tons"/>
        </div>
        <Button onClick={run} disabled={loading} data-testid="suggest-btn" className="h-10 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full px-5">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Suggest <ArrowRight className="ml-1 w-4 h-4"/></>}
        </Button>
      </div>

      {result && (
        <div className="mt-5 border-t border-stone-200 pt-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Diversified portfolio · {result.items.length} projects</div>
              <div className="text-2xl font-light mt-1" data-testid="suggest-total">{format(result.estimated_total_usd)}</div>
            </div>
            <Button onClick={addAll} data-testid="suggest-add-all" className={`rounded-full ${added ? "bg-emerald-600 hover:bg-emerald-600" : "bg-[#D97757] hover:bg-[#c26343]"} text-white`}>
              {added ? <><Check className="w-4 h-4 mr-1"/> Added</> : "Add all to cart"}
            </Button>
          </div>
          <div className="space-y-2">
            {result.items.map((i, idx) => (
              <Link key={idx} to={`/project/${i.project.id}`} data-testid={`suggest-item-${idx}`} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors">
                <img src={i.project.image_url} alt="" className="w-12 h-12 rounded-lg object-cover"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{i.project.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5 truncate">{i.rationale}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{i.tons.toLocaleString()} t</div>
                  <div className="text-xs text-stone-500">{format(i.line_amount)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
